import * as React from 'react'

import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material'

import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import Diversity1OutlinedIcon from '@mui/icons-material/Diversity1Outlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InsertChartOutlinedTwoToneIcon from '@mui/icons-material/InsertChartOutlinedTwoTone';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const BG           = '#3b82f6';
const ACTIVE_BG    = 'rgba(255,255,255,0.2)';
const HOVER_BG     = 'rgba(255,255,255,0.12)';
const ACTIVE_COLOR   = '#ffffff';
const INACTIVE_COLOR = 'rgba(255,255,255,0.72)';
const EXPANDED_COLOR = 'rgba(255,255,255,0.9)';
const ACCENT       = 'linear-gradient(180deg, #ffffff 0%, #bfdbfe 100%)';
const BORDER       = 'rgba(255,255,255,0.15)';

const menuItems = [
  { id: 0, icon: <InsertChartOutlinedTwoToneIcon />, label: 'Dashboard' },
  {
    id: 1, icon: <DescriptionOutlinedIcon />, label: 'Data Source',
    hasSubmenu: true,
    submenuItems: [
      { id: 1, icon: <DescriptionOutlinedIcon />, label: 'Data Source' },
      { id: 5, icon: <TuneIcon />, label: 'Dimension Defaults' },
    ],
  },
  {
    id: 9, icon: <SchoolOutlinedIcon />, label: 'Knowledgebase',
    hasSubmenu: true,
    submenuItems: [
      { id: 10, icon: <PsychologyOutlinedIcon />, label: 'Member Embeddings' },
    ],
  },
  { id: 2, icon: <PeopleAltOutlinedIcon />, label: 'User' },
  { id: 3, icon: <Diversity1OutlinedIcon />, label: 'Group' },
  { id: 4, icon: <VerifiedOutlinedIcon />, label: 'Permission' },
  { id: 6, icon: <MailOutlinedIcon />, label: 'Inbox' },
  { id: 7, icon: <HelpOutlineOutlinedIcon />, label: 'Help' },
  { id: 8, icon: <SettingsOutlinedIcon />, label: 'Setting' },
];

function Sidebar(props) {
  const { value, onChange } = props;
  const [dataSourceOpen, setDataSourceOpen] = React.useState(false);
  const [knowledgebaseOpen, setKnowledgebaseOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(false);

  const isOpen = (id) => (id === 1 && dataSourceOpen) || (id === 9 && knowledgebaseOpen);

  const toggleSubmenu = (id) => {
    if (id === 1) setDataSourceOpen(o => !o);
    else if (id === 9) setKnowledgebaseOpen(o => !o);
  };

  const renderItem = (item, sub = false) => {
    const active   = value === item.id && !item.hasSubmenu;
    const expanded = item.hasSubmenu && isOpen(item.id);

    const button = (
      <ListItemButton
        onClick={() => item.hasSubmenu ? toggleSubmenu(item.id) : onChange(item.id)}
        sx={{
          minHeight: sub ? 38 : 44,
          borderRadius: '9px',
          px: '10px',
          pl: !collapsed && sub ? '24px' : '10px',
          gap: '10px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          color: active ? ACTIVE_COLOR : expanded ? EXPANDED_COLOR : INACTIVE_COLOR,
          backgroundColor: active ? ACTIVE_BG : 'transparent',
          position: 'relative',
          transition: 'background 0.15s ease, color 0.15s ease',
          '&::before': active ? {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '22%',
            bottom: '22%',
            width: '3px',
            background: ACCENT,
            borderRadius: '0 3px 3px 0',
          } : {},
          '&:hover': {
            backgroundColor: active ? ACTIVE_BG : HOVER_BG,
            color: active ? ACTIVE_COLOR : EXPANDED_COLOR,
          },
        }}
      >
        <ListItemIcon sx={{ color: 'inherit', minWidth: 'auto', '& .MuiSvgIcon-root': { fontSize: sub ? '16px' : '19px' } }}>
          {item.icon}
        </ListItemIcon>

        {!collapsed && (
          <>
            <ListItemText
              primary={item.label}
              sx={{
                '& .MuiListItemText-primary': {
                  fontSize: sub ? '0.835rem' : '0.885rem',
                  fontWeight: active ? 600 : expanded ? 500 : 400,
                  letterSpacing: '-0.1px',
                  color: 'inherit',
                  whiteSpace: 'nowrap',
                },
              }}
            />
            {item.hasSubmenu && (
              <ExpandMore sx={{
                fontSize: '17px',
                color: 'inherit',
                transition: 'transform 0.2s ease',
                transform: isOpen(item.id) ? 'rotate(180deg)' : 'none',
              }} />
            )}
          </>
        )}
      </ListItemButton>
    );

    return (
      <ListItem key={item.id} disablePadding sx={{ mb: '2px', px: '8px' }}>
        {collapsed
          ? <Tooltip title={item.label} placement="right" arrow>{button}</Tooltip>
          : button
        }
      </ListItem>
    );
  };

  return (
    <Box
      sx={{
        width: collapsed ? 68 : 240,
        height: '100vh',
        flexShrink: 0,
        background: BG,
        borderRight: `1px solid ${BORDER}`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Logo + Toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          px: collapsed ? '10px' : '14px',
          py: '13px',
          borderBottom: `1px solid ${BORDER}`,
          justifyContent: collapsed ? 'center' : 'space-between',
          overflow: 'hidden',
          transition: 'padding 0.25s ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', overflow: 'hidden' }}>
          <Box sx={{
            width: 30, height: 30, flexShrink: 0,
            borderRadius: '8px',
            background: 'rgba(255,255,255,0.22)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: '#fff', letterSpacing: '0.5px' }}>
              AC
            </Typography>
          </Box>
          {!collapsed && (
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#fff', letterSpacing: '-0.2px', whiteSpace: 'nowrap' }}>
              AnalyticChat
            </Typography>
          )}
        </Box>

        {!collapsed && (
          <IconButton
            onClick={() => setCollapsed(true)}
            size="small"
            sx={{
              color: 'rgba(255,255,255,0.7)',
              flexShrink: 0,
              '&:hover': { background: 'rgba(255,255,255,0.15)', color: '#fff' },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: '20px' }} />
          </IconButton>
        )}
      </Box>

      {/* Collapsed expand button — first nav slot */}
      {collapsed && (
        <Box sx={{ px: '8px', pt: '8px' }}>
          <Tooltip title="Expand sidebar" placement="right" arrow>
            <IconButton
              onClick={() => setCollapsed(false)}
              sx={{
                width: '100%',
                borderRadius: '9px',
                color: 'rgba(255,255,255,0.7)',
                py: '10px',
                '&:hover': { background: HOVER_BG, color: '#fff' },
              }}
            >
              <ChevronRightIcon sx={{ fontSize: '20px' }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Nav */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: '10px',
          '&::-webkit-scrollbar': { width: '3px' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.3)', borderRadius: '4px' },
        }}
      >
        <List component="nav" sx={{ p: 0 }}>
          {menuItems.map((item) => (
            <React.Fragment key={item.id}>
              {renderItem(item)}
              {!collapsed && item.hasSubmenu && (
                <Collapse in={isOpen(item.id)} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.submenuItems.map(sub => renderItem(sub, true))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>

      {/* Footer */}
      {!collapsed && (
        <Box sx={{ px: '20px', py: '12px', borderTop: `1px solid ${BORDER}` }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', textAlign: 'center', fontWeight: 500 }}>
            v1.0.0 • AnalyticChat
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default Sidebar;
